/**
 * Shared utility for the `numberUp` propertyCharacteristic. The rating
 * engine on both sides MUST use the exact same logic for cell-key
 * matching to agree.
 *
 * The constants `72 * 6 = 432` and `72 * 9 = 648` (PostScript points;
 * 72pt = 1 inch) match the existing manufacturing-side bucketing
 * in `replenishment-selector.component.ts`. The enum is extensible —
 * future buckets (e.g. `'8UP'`, `'OVERSIZE'`) require simultaneous
 * updates on both sides plus a contract bump.
 *
 * See `docs/contracts/shared/RateConfig.md` "numberUp derivation".
 */

/** Active values; extensible. Add a new constant here when adding a bucket. */
export type NumberUp = '2UP' | '4UP';

/** All known {@link NumberUp} values, in stable order. */
export const NUMBER_UP_VALUES: NumberUp[] = ['2UP', '4UP'];

const HEIGHT_BREAK_PT = 72 * 9; // 648 pt = 9"
const WIDTH_BREAK_PT  = 72 * 6; // 432 pt = 6"

/**
 * Classify a body's dimensions into a {@link NumberUp} bucket.
 *
 * Returns `'2UP'` when EITHER dimension exceeds its break point (the
 * book is too large to ship on a 4-up sheet). Returns `'4UP'` otherwise.
 *
 * Inputs are PostScript points and may come from `body.width/height`
 * or the equivalent mutable-body fields. Non-finite or non-positive
 * inputs default to `'4UP'` (the smaller bucket) — this matches the
 * manufacturing helper's permissive behavior; the rating engine should
 * separately validate that an item carries a real body before pricing.
 */
export function computeNumberUp(widthPt: number | undefined | null,
                                heightPt: number | undefined | null): NumberUp {
  const w = (typeof widthPt === 'number' && isFinite(widthPt) && widthPt > 0) ? widthPt : 0;
  const h = (typeof heightPt === 'number' && isFinite(heightPt) && heightPt > 0) ? heightPt : 0;
  if (w > WIDTH_BREAK_PT || h > HEIGHT_BREAK_PT) { return '2UP'; }
  return '4UP';
}

